import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveSiteSeo } from '../siteSeo';
import { fetchSite, getToken, patchSeo, uploadFile } from './api';
import { fileInput, input, label } from './adminUi';

function seoFormFromResolved(resolved) {
  return {
    htmlTitle: resolved.htmlTitle,
    socialTitle: resolved.socialTitle,
    description: resolved.description,
    socialDescription: resolved.socialDescription,
    keywords: resolved.keywords,
    author: resolved.author,
    siteName: resolved.siteName,
    shareImageUrl: resolved.shareImageUrl,
    shareImageAlt: resolved.shareImageAlt,
    canonicalUrl: resolved.canonicalUrl,
    themeColor: resolved.themeColor,
    robots: resolved.robots,
    ogLocale: resolved.ogLocale,
  };
}

export default function SeoDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => seoFormFromResolved(resolveSiteSeo(DEFAULT_SITE)));
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingShare, setUploadingShare] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setForm(seoFormFromResolved(resolveSiteSeo(site))))
      .catch(() => setForm(seoFormFromResolved(resolveSiteSeo(DEFAULT_SITE))));
  }, [navigate]);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onShareImageUpload(file) {
    if (!file) return;
    setErr('');
    setMsg('');
    setUploadingShare(true);
    try {
      const { url } = await uploadFile(file);
      setField('shareImageUrl', url);
      setMsg('Share image uploaded to Cloudinary. Click Save to publish.');
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadingShare(false);
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const { seo } = await patchSeo(form);
      setForm(seoFormFromResolved(resolveSiteSeo({ seo: seo || form })));
      setMsg('Saved. Meta tags update on the public site after refresh.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="z-10 flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-neutral-900">SEO &amp; social meta</h1>
          <p className="text-sm text-neutral-600">
            Page title, description, Open Graph, Twitter, and PWA title
          </p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-lg bg-brand-olive px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="grid w-full gap-8 xl:grid-cols-2 xl:items-start">
        <div className="grid min-w-0 w-full gap-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm md:p-6">
          <h2 className="text-sm font-bold text-neutral-900">Browser &amp; search</h2>
          <label className={label}>
            HTML title (browser tab)
            <input className={input} value={form.htmlTitle} onChange={(e) => setField('htmlTitle', e.target.value)} />
          </label>
          <label className={label}>
            Meta description
            <textarea
              className={`${input} min-h-[5rem]`}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </label>
          <label className={label}>
            Keywords (comma-separated)
            <input className={input} value={form.keywords} onChange={(e) => setField('keywords', e.target.value)} />
          </label>
          <label className={label}>
            Author
            <input className={input} value={form.author} onChange={(e) => setField('author', e.target.value)} />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Theme color (hex)
              <input className={input} value={form.themeColor} onChange={(e) => setField('themeColor', e.target.value)} />
            </label>
            <label className={label}>
              Robots
              <input className={input} value={form.robots} onChange={(e) => setField('robots', e.target.value)} />
            </label>
          </div>
          <label className={label}>
            Canonical URL (optional, full URL — leave empty to use current page URL on the client)
            <input
              className={`${input} font-mono text-sm`}
              value={form.canonicalUrl}
              onChange={(e) => setField('canonicalUrl', e.target.value)}
              placeholder="https://example.com/"
            />
          </label>
        </div>

        <div className="grid min-w-0 w-full gap-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm md:p-6">
          <h2 className="text-sm font-bold text-neutral-900">Open Graph &amp; Twitter</h2>
          <label className={label}>
            Site / brand name
            <input className={input} value={form.siteName} onChange={(e) => setField('siteName', e.target.value)} />
          </label>
          <label className={label}>
            Social title (og:title / Twitter — can differ from HTML title)
            <input className={input} value={form.socialTitle} onChange={(e) => setField('socialTitle', e.target.value)} />
          </label>
          <label className={label}>
            Social description (optional; falls back to meta description if empty in resolver)
            <textarea
              className={`${input} min-h-[4rem]`}
              value={form.socialDescription}
              onChange={(e) => setField('socialDescription', e.target.value)}
            />
          </label>
          <label className={label}>
            og:locale (e.g. en_US)
            <input className={input} value={form.ogLocale} onChange={(e) => setField('ogLocale', e.target.value)} />
          </label>

          <div className="rounded-lg border border-neutral-100 bg-neutral-50/80 p-4">
            <span className={`${label} mb-0`}>Share image</span>
            <div className="mt-2 aspect-[1.91/1] max-h-56 w-full overflow-hidden rounded-lg border border-neutral-200 bg-white">
              {form.shareImageUrl ? (
                <img src={form.shareImageUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full min-h-[6rem] items-center justify-center text-xs text-neutral-400">
                  No image
                </div>
              )}
            </div>
            <label className={`${label} mt-3 mb-0`}>
              Upload image
              <input
                type="file"
                accept="image/*"
                disabled={uploadingShare}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onShareImageUpload(f);
                }}
              />
            </label>
            {uploadingShare ? <p className="mt-2 text-xs text-neutral-500">Uploading…</p> : null}
            <label className={`${label} mt-3`}>
              Image URL
              <input
                className={`${input} mt-1 font-mono text-sm`}
                value={form.shareImageUrl}
                onChange={(e) => setField('shareImageUrl', e.target.value)}
              />
            </label>
            <label className={label}>
              Image alt text
              <input className={input} value={form.shareImageAlt} onChange={(e) => setField('shareImageAlt', e.target.value)} />
            </label>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
